/**
 * useFokusMode Hook
 * 
 * Shared FOKUS mode logic for Kanban, Table, and other views.
 * Uses the same dependency calculation as Flow view's FOKUS mode.
 * 
 * FOKUS MODE LOGIC:
 * When you focus on task(s), you see:
 * 1. The selected/filtered task(s) - the "target"
 * 2. ALL incomplete upstream dependencies recursively (until completed or no deps)
 * 3. +1 downstream (immediate children only)
 * 
 * This answers: "What do I need to complete to work on X?"
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { calculateAllFocusedTaskIds, buildDecisionAnswersMap } from '../FlowchartView/hooks/utils/focusModeUtils';
import { calculateLockStates } from '../FlowchartView/utils/dependencyUtils';
import { useWaitGateNow } from './ConditionsDropdown/utils/useWaitGateNow';

/**
 * @param {Object} params
 * @param {Array} params.tasks - All tasks in the campaign
 * @param {Array} params.statuses - Status configurations
 * @param {Array} params.nestedSprints - Nested sprint data
 * @param {Set} params.selectedTaskIds - Currently selected task IDs (from view's selection state)
 * @param {Function} params.onFokusChange - Optional callback when FOKUS mode changes (isActive, focusedTaskIds)
 * @returns {Object} FOKUS mode state and handlers
 */
const useFokusMode = ({ tasks = [], statuses = [], nestedSprints = [], selectedTaskIds = new Set(), onFokusChange }) => {
  // Core FOKUS state
  const [isFokusMode, setIsFokusMode] = useState(false);
  const [fokusedTaskIds, setFokusedTaskIds] = useState(null); // Primary focused task IDs (Set or null)
  
  // Track previous focus mode state for exit detection
  const prevFokusModeRef = useRef(false);
  
  // Use ref for callback to avoid infinite loops when callback changes
  const onFokusChangeRef = useRef(onFokusChange);
  useEffect(() => {
    onFokusChangeRef.current = onFokusChange;
  }, [onFokusChange]);
  
  // Build decision answers map for dependency calculation
  const decisionAnswers = useMemo(() => buildDecisionAnswersMap(tasks), [tasks]);
  
  // Calculate lock states for actionable task detection
  const waitNow = useWaitGateNow(tasks);
  const lockStates = useMemo(() => {
    return calculateLockStates(tasks, nestedSprints, statuses, waitNow ?? Date.now());
  }, [tasks, nestedSprints, statuses, waitNow]);
  
  // Calculate all focused task IDs (including dependencies)
  // Note: calculateAllFocusedTaskIds returns "allFocusedTaskIds" (with 'c'), we rename to "allFokusedTaskIds" (with 'k')
  const { allFocusedTaskIds: allFokusedTaskIds, hiddenConnections, primaryFocusedIds: primaryFokusedIds } = useMemo(() => {
    if (!isFokusMode || !fokusedTaskIds) {
      return { allFocusedTaskIds: null, hiddenConnections: {}, primaryFocusedIds: null };
    }
    
    // Ensure fokusedTaskIds is a Set
    const fokusedSet = fokusedTaskIds instanceof Set ? fokusedTaskIds : new Set(fokusedTaskIds);
    
    return calculateAllFocusedTaskIds({
      focusedTaskIds: fokusedSet,
      tasks,
      decisionAnswers,
      statuses,
    });
  }, [isFokusMode, fokusedTaskIds, tasks, decisionAnswers, statuses]);
  
  // Filter tasks to only show focused ones
  const fokusFilteredTasks = useMemo(() => {
    if (!isFokusMode || !allFokusedTaskIds) {
      return tasks;
    }
    return tasks.filter(task => allFokusedTaskIds.has(task.id));
  }, [tasks, isFokusMode, allFokusedTaskIds]);
  
  // Enter FOKUS mode with specific task IDs
  const enterFokusMode = useCallback((taskIds) => {
    if (!taskIds || taskIds.length === 0) return;
    
    const taskIdSet = new Set(taskIds);
    setFokusedTaskIds(taskIdSet);
    setIsFokusMode(true);
    
    // Notify callback (use ref to avoid dependency issues)
    onFokusChangeRef.current?.(true, taskIdSet);
  }, []);
  
  // Exit FOKUS mode
  const exitFokusMode = useCallback(() => {
    setIsFokusMode(false);
    setFokusedTaskIds(null);
    
    // Notify callback (use ref to avoid dependency issues)
    onFokusChangeRef.current?.(false, null);
  }, []);
  
  // Focus on selected tasks (from selection state)
  const fokusSelected = useCallback((taskIdsToFocus) => {
    // Use provided taskIds or fall back to selectedTaskIds prop
    const ids = taskIdsToFocus || selectedTaskIds;
    if (!ids || ids.size === 0) return;
    enterFokusMode(Array.from(ids));
  }, [enterFokusMode, selectedTaskIds]);
  
  // Focus on actionable tasks
  const fokusActionable = useCallback(() => {
    const actionableIds = tasks
      .filter(task => {
        const lockState = lockStates[task.id];
        return lockState && !lockState.isLocked && !lockState.isCompleted;
      })
      .map(task => task.id);
    
    if (actionableIds.length > 0) {
      enterFokusMode(actionableIds);
    }
  }, [tasks, lockStates, enterFokusMode]);
  
  // Focus by filter criteria
  const fokusByFilter = useCallback((matchingTaskIds) => {
    if (matchingTaskIds.length > 0) {
      enterFokusMode(matchingTaskIds);
    }
  }, [enterFokusMode]);
  
  // Update previous focus mode ref
  useEffect(() => {
    prevFokusModeRef.current = isFokusMode;
  }, [isFokusMode]);
  
  // Check if a task is in the primary focused set (not just a dependency)
  const isPrimaryFokused = useCallback((taskId) => {
    return primaryFokusedIds?.has(taskId) || false;
  }, [primaryFokusedIds]);
  
  // Check if a task is a dependency (shown but faded)
  const isDependencyTask = useCallback((taskId) => {
    if (!allFokusedTaskIds || !primaryFokusedIds) return false;
    return allFokusedTaskIds.has(taskId) && !primaryFokusedIds.has(taskId);
  }, [allFokusedTaskIds, primaryFokusedIds]);
  
  return {
    // State
    isFokusMode,
    fokusedTaskIds: allFokusedTaskIds, // All visible task IDs (including deps)
    primaryFokusedIds, // Just the originally selected/filtered tasks
    hiddenConnections,
    fokusFilteredTasks, // Tasks filtered to only show focused ones
    lockStates,
    selectedTaskIds, // Pass through for toolbar
    
    // Actions
    enterFokusMode,
    exitFokusMode,
    fokusSelected,
    fokusActionable,
    fokusByFilter,
    
    // Helpers
    isPrimaryFokused,
    isDependencyTask,
  };
};

export default useFokusMode;
